import type { CreatePlayerRequest, PlayerSummary, TournamentResult, TournamentStaffDetail } from '@brush/api-client';

/**
 * One line of a payout sheet as it is being typed, before it is anything the API would accept.
 *
 * **Place is not stored here.** A row's finishing position is its position in the list, assigned
 * at save from the order on screen — so the only way to change a place is to move the row, and
 * two rows can never claim the same one. That rules out by construction what the database
 * enforces with a unique index on `(TournamentId, Position)`.
 *
 * Every numeric field is held as text. A half-typed "1" in a prize box is not the number 1, and
 * storing it as one makes the box fight the person filling it in — the same reason the tournament
 * form keeps its money fields as strings until save.
 *
 * A row carries the player's details whether or not the player exists yet. For somebody the room
 * already has they are an echo of the stored record and read-only; for somebody new they are what
 * the player will be created from. The distinction is `playerId === null`, not a separate shape,
 * so the grid renders one row type.
 */
export interface ResultDraft {
  /** Stable across reordering and deletion, which an index is not. */
  key: string;
  /**
   * Whether this entrant reached a paid place. False means they played and busted before the
   * money — a real row, with a buy-in count and no place.
   *
   * The grid keeps the two apart rather than putting a place box on every row: place is still the
   * row's position, but only within the paid section, so moving a row is still the only way to
   * change one and the two can never disagree.
   */
  placed: boolean;
  /**
   * An unclaimed place (ADR 0013): a place the sheet records while nobody has claimed it. Its own
   * state, deliberately — `playerId: null` already means "this row will create a player", and an
   * unclaimed place read that way would block the save until somebody invented a name for it.
   */
  unclaimed: boolean;
  /** The matched player, or null when this row will create one (or is an unclaimed place). */
  playerId: string | null;
  cardId: string;
  firstName: string;
  lastName: string;
  city: string;
  state: string;
  prize: string;
  prizeNotes: string;
  isInDeal: boolean;
  buyins: string;
  /**
   * The card the results file gave a place nobody had claimed, carried through untouched so a save
   * does not drop it. Provenance only — never matched against a player or offered as a card.
   */
  sourceCardId: string | null;
}

let nextKey = 0;
const newKey = () => `r${nextKey++}`;

const emptyFields = {
  unclaimed: false,
  prize: '',
  prizeNotes: '',
  isInDeal: false,
  buyins: '',
  sourceCardId: null,
};

/** A row for somebody the room already carries. Their details ride along for display. */
export function draftForPlayer(player: PlayerSummary, placed = true): ResultDraft {
  return {
    key: newKey(),
    placed,
    playerId: player.id,
    cardId: player.cardId,
    firstName: player.firstName,
    lastName: player.lastName,
    city: player.city ?? '',
    state: player.state ?? '',
    ...emptyFields,
  };
}

/**
 * A row for somebody the room has never carded. Seeded with whatever the search box held, which
 * is most likely the card ID — a payout sheet leads with it.
 */
export function draftForNewPlayer(cardId: string, placed = true): ResultDraft {
  return {
    key: newKey(),
    placed,
    playerId: null,
    cardId: cardId.trim(),
    firstName: '',
    lastName: '',
    city: '',
    state: '',
    ...emptyFields,
  };
}

/**
 * Re-opening a tournament that already has results. Sorted by the stored place, because that
 * order *is* the place once the sheet is back on screen. Each row's details come from the finisher
 * the tournament's own read names (performance rank 2), so the room's roster is not needed.
 */
export function draftsFromTournament(tournament: TournamentStaffDetail): ResultDraft[] {
  return tournament.results
    .slice()
    // Paid places in order, then the rest of the field. A null place sorts before 1 otherwise,
    // which would open the sheet on the bust-outs.
    .sort((a, b) => {
      if (a.position === null && b.position === null) return 0;
      if (a.position === null) return 1;
      if (b.position === null) return -1;
      return a.position - b.position;
    })
    .map((r) => {
      const player = r.player;
      return {
        key: newKey(),
        placed: r.position !== null,
        unclaimed: r.unclaimed,
        playerId: r.playerId,
        cardId: player?.cardId ?? '',
        firstName: player?.firstName ?? '',
        lastName: player?.lastName ?? '',
        city: player?.city ?? '',
        state: player?.state ?? '',
        prize: String(r.prize),
        prizeNotes: r.prizeNotes ?? '',
        isInDeal: r.isInDeal,
        buyins: r.buyins === null ? '' : String(r.buyins),
        sourceCardId: r.sourceCardId,
      };
    });
}

const asInt = (value: string): number | null => {
  const trimmed = value.trim();
  if (trimmed === '') return null;
  return /^\d+$/.test(trimmed) ? Number.parseInt(trimmed, 10) : Number.NaN;
};

/**
 * Everything wrong with the sheet, in the order an operator would fix it. Reported as a list
 * rather than a first error: a payout sheet is entered in one pass, and being told about one
 * problem at a time turns a single correction round into ten.
 *
 * Nothing here checks places. They are the row order, so they cannot collide or be malformed.
 */
export function resultProblems(rows: ResultDraft[]): string[] {
  const problems: string[] = [];
  if (rows.length === 0) return ['Add at least one player.'];

  const seenCards = new Map<string, number>();

  let place = 0;
  rows.forEach((d) => {
    // Named the way the grid labels it, so an operator can find the row being complained about.
    const where = d.placed ? `Place ${(place += 1)}` : `${d.firstName} ${d.lastName}`.trim() || 'An entrant';

    if (d.unclaimed) {
      // A place nobody has claimed, so there is no player to describe — but it is a place.
      if (!d.placed) problems.push(`${where}: an unclaimed place needs a place. Move it back into the paid places.`);
    } else if (d.playerId === null) {
      if (d.cardId.trim() === '') problems.push(`${where}: a new player needs a card ID.`);
      if (d.firstName.trim() === '') problems.push(`${where}: a new player needs a first name.`);
      if (d.lastName.trim() === '') problems.push(`${where}: a new player needs a last name.`);

      // Two new rows claiming one card would be rejected halfway through creating them, leaving
      // some players made and some not. An existing player cannot repeat — the search box will
      // not offer one already on the sheet.
      const card = d.cardId.trim().toLowerCase();
      if (card !== '') {
        const first = seenCards.get(card);
        if (first !== undefined) problems.push(`${where}: card ${d.cardId.trim()} is on this sheet twice.`);
        else seenCards.set(card, 1);
      }
    }

    const prize = asInt(d.prize);
    if (prize === null) problems.push(`${where}: prize is required — enter 0 for a place that paid nothing.`);
    else if (Number.isNaN(prize)) problems.push(`${where}: prize must be a whole number of dollars.`);
    // Paid but nowhere. The same contradiction the importer refuses, caught here before the row
    // could be saved with money against no finish.
    else if (!d.placed && prize > 0) {
      problems.push(`${where}: won $${prize} but is listed as not placing. Move them into the paid places.`);
    }

    const buyins = asInt(d.buyins);
    if (buyins !== null && (Number.isNaN(buyins) || buyins < 1)) {
      problems.push(`${where}: buy-ins must be a whole number, 1 or more, or left blank.`);
    }
  });

  return problems;
}

/** The players this sheet will have to create before the results can reference them. */
export function playersToCreate(
  rows: ResultDraft[],
  roomId: string,
): (CreatePlayerRequest & { key: string })[] {
  return rows
    .filter((d) => d.playerId === null && !d.unclaimed)
    .map((d) => ({
      key: d.key,
      roomId,
      cardId: d.cardId.trim(),
      firstName: d.firstName.trim(),
      lastName: d.lastName.trim(),
      city: d.city.trim() === '' ? null : d.city.trim(),
      state: d.state.trim() === '' ? null : d.state.trim(),
      avatar: initials(d.firstName, d.lastName),
      // The grid records a finish, not a customer record. A contact is added on the player's own
      // form, where it can be seen and corrected — see room-web's Players screen.
      email: null,
      phone: null,
    }));
}

/** Same rule the add-player dialog uses, so a player made here looks like one made there. */
export function initials(first: string, last: string): string {
  const letters = [first, last].map((part) => part.trim()[0]?.toUpperCase() ?? '').join('');
  return letters === '' ? '??' : letters;
}

/**
 * The rows as the API wants them, place taken from the order. `createdIds` maps a draft key to
 * the player id its creation returned, so rows that made a player and rows that matched one
 * converge here.
 */
export function toResults(
  rows: ResultDraft[],
  createdIds: Map<string, string>,
): TournamentResult[] {
  // Places are numbered over the paid rows alone, so the sequence stays 1..K whatever the rest of
  // the field does. `rows` is already ordered paid-first by the grid.
  let place = 0;
  return rows.map((d) => ({
    // An unclaimed place is sent as one: explicitly, with no player (ADR 0013).
    playerId: d.unclaimed ? null : (d.playerId ?? createdIds.get(d.key) ?? ''),
    position: d.placed ? (place += 1) : null,
    prize: Number.parseInt(d.prize, 10),
    prizeNotes: d.prizeNotes.trim() === '' ? null : d.prizeNotes.trim(),
    isInDeal: d.isInDeal,
    buyins: d.buyins.trim() === '' ? null : Number.parseInt(d.buyins, 10),
    unclaimed: d.unclaimed,
    sourceCardId: d.sourceCardId,
    // A read-only field on a write; the server ignores it.
    id: null,
  }));
}

/**
 * Paid rows first, everything else after, each group keeping its own order.
 *
 * The invariant the grid relies on: place is the row's position *within the paid group*, so that
 * group has to be a contiguous run at the top for the numbering to mean anything.
 */
export function inPlaceOrder(rows: ResultDraft[]): ResultDraft[] {
  return [...rows.filter((r) => r.placed), ...rows.filter((r) => !r.placed)];
}

/** Moving a row is how a place is changed, so it is the one reorder primitive. */
export function moveDraft(rows: ResultDraft[], index: number, delta: number): ResultDraft[] {
  const target = index + delta;
  if (target < 0 || target >= rows.length) return rows;
  // Only within the paid group: a swap across the boundary would silently pay somebody or stop
  // paying them, which is what the toggle is for.
  if (rows[index]!.placed !== rows[target]!.placed) return rows;
  const next = rows.slice();
  const [moved] = next.splice(index, 1);
  next.splice(target, 0, moved!);
  return next;
}

/**
 * Moves one entrant between "finished in the money" and "played, didn't place".
 *
 * A row leaving the paid places joins the end of the field; one entering takes the last paid
 * place, because a row promoted into the middle would renumber everyone below it for a reason
 * the operator did not ask for.
 */
export function togglePlaced(rows: ResultDraft[], key: string): ResultDraft[] {
  return inPlaceOrder(rows.map((r) => (r.key === key ? { ...r, placed: !r.placed } : r)));
}

/**
 * Claims an unclaimed place for a player the room already holds. The row keeps its place, its
 * figures and the card its file gave it — that provenance survives a claim — and only who holds it
 * changes.
 */
export function assignExistingPlayer(rows: ResultDraft[], key: string, player: PlayerSummary): ResultDraft[] {
  return rows.map((d) =>
    d.key !== key
      ? d
      : {
          ...d,
          unclaimed: false,
          playerId: player.id,
          cardId: player.cardId,
          firstName: player.firstName,
          lastName: player.lastName,
          city: player.city ?? '',
          state: player.state ?? '',
        },
  );
}

/**
 * Claims an unclaimed place for somebody the room has never carded: the row becomes a new player's,
 * seeded with the card typed into the search, and the player is created on save like any other.
 * Their own card, never the one the file generated — that stays as provenance.
 */
export function assignNewPlayer(rows: ResultDraft[], key: string, cardId: string): ResultDraft[] {
  return rows.map((d) =>
    d.key !== key
      ? d
      : { ...d, unclaimed: false, playerId: null, cardId: cardId.trim(), firstName: '', lastName: '', city: '', state: '' },
  );
}

/**
 * Returns a claimed place to unclaimed: nobody holds it again. Its place, figures and provenance
 * stay. Only a paid place can be unclaimed, so an unplaced row is left as it is.
 */
export function returnToUnclaimed(rows: ResultDraft[], key: string): ResultDraft[] {
  return rows.map((d) =>
    d.key !== key || !d.placed
      ? d
      : { ...d, unclaimed: true, playerId: null, cardId: '', firstName: '', lastName: '', city: '', state: '' },
  );
}
